import { Injectable, WritableSignal } from "@angular/core";
import { JobIssue, JobIssueVendor } from "../../../core/database/db";
import { SELF_ASSIGNED_VENDOR, SELF_ASSIGNED_VENDOR_ASSIGNMENT, VendorDTO } from "../models/VendorDTO";
import { JobIssueService } from "../../jobIssues/services/job-issue-service";
import { JobIssueVendorService } from "../../jobIssues/services/job-issue-vendor.service";

/**
 * Persists `isSelfAssigned` for one kind of issue. `JobIssueService` (the default) handles job issues;
 * `AssertionIssuesService` handles checklist (assertion) issues, which IndexedDB keeps in their own table.
 */
export interface SelfAssignmentService<TIssue extends JobIssue> {
  assignIssueToMe(issue: TIssue): Promise<unknown>;
  unassignIssueFromMe(issue: TIssue): Promise<unknown>;
}

/**
 * Shared vendor/self-assignment logic for the page data services (Open Items, BQ inspection steps, BQ9).
 * Persists through the issue domain services and keeps the caller's issue/`jobIssueVendors` signals in
 * sync, so each data service only needs a thin wrapper that passes its own signals in. Works for any
 * `JobIssue`, including `AssertionIssue` (which extends it) when given `AssertionIssuesService`.
 */
@Injectable({ providedIn: "root" })
export class VendorDataHelperService {

  constructor(
    private jobIssueService: JobIssueService,
    private jobIssueVendorService: JobIssueVendorService
  ) { }

  /**
   * The assignment a single-assignee vendor select shows for an issue: the CM's own self-assignment when
   * `isSelfAssigned`, otherwise its active trade partner assignment, otherwise none.
   */
  getVendorAssignmentForIssue(issue: JobIssue, jobIssueVendors: JobIssueVendor[] | undefined): JobIssueVendor | null {
    if (issue.isSelfAssigned) {
      return { ...SELF_ASSIGNED_VENDOR_ASSIGNMENT, issueIdentifier: issue.identifier };
    }

    return (jobIssueVendors ?? []).find(v => v.issueIdentifier === issue.identifier && !v.deleted) ?? null;
  }

  /**
   * Assigns an issue to a trade partner or to the CM ("Assign to Me"), replacing the given previous
   * assignment. Self-assignment is stored on the issue (`isSelfAssigned`) rather than as a vendor record,
   * so a self-assigned issue is never sent to a trade partner.
   */
  async addOrReplaceVendorAssignmentForIssue<TIssue extends JobIssue>(
    issue: TIssue,
    selectedVendor: VendorDTO | null,
    previousSelectedVendorAssignmentToReplace: JobIssueVendor | null,
    jobIssueVendors: WritableSignal<JobIssueVendor[] | undefined>,
    issues: WritableSignal<TIssue[] | undefined>,
    selfAssignmentService: SelfAssignmentService<TIssue> = this.jobIssueService
  ): Promise<JobIssueVendor | null> {
    const didSelectAssignToMe = selectedVendor?.id === SELF_ASSIGNED_VENDOR.id;
    const didRemoveAssignToMe = previousSelectedVendorAssignmentToReplace?.externalVendorId === SELF_ASSIGNED_VENDOR.id;

    if (didSelectAssignToMe && didRemoveAssignToMe) {
      return null;
    }

    if (didSelectAssignToMe) {
      await selfAssignmentService.assignIssueToMe(issue);
      this.setIssueSelfAssigned(issue.identifier, true, issues);
    }
    if (didRemoveAssignToMe) {
      await selfAssignmentService.unassignIssueFromMe(issue);
      this.setIssueSelfAssigned(issue.identifier, false, issues);
    }

    const vendorToAssign = didSelectAssignToMe ? null : selectedVendor;
    const vendorToRemove = didRemoveAssignToMe ? null : previousSelectedVendorAssignmentToReplace;

    const wentFromNoVendorToSelfAssign = !!vendorToAssign === false && !!vendorToRemove === false;
    if (wentFromNoVendorToSelfAssign) {
      return null;
    }

    const newOrUpdatedVendorAssignment = await this.jobIssueVendorService.addOrReplaceVendorAssignmentForIssue(
      issue.identifier,
      vendorToAssign,
      vendorToRemove
    );

    jobIssueVendors.update(vendorAssignments => {
      const existing = vendorAssignments ?? [];

      if (newOrUpdatedVendorAssignment.deleted) {
        return existing.filter(i => i.issueVendorIdentifier !== newOrUpdatedVendorAssignment.issueVendorIdentifier);
      }

      let didUpdateExistingAssignment = false;
      const updatedVendors = existing.map(vendorAssignment => {
        if (vendorAssignment.issueVendorIdentifier === newOrUpdatedVendorAssignment.issueVendorIdentifier) {
          didUpdateExistingAssignment = true;
          return newOrUpdatedVendorAssignment;
        }
        return vendorAssignment;
      });

      if (didUpdateExistingAssignment) {
        return updatedVendors;
      }

      return [...updatedVendors, newOrUpdatedVendorAssignment];
    });

    return newOrUpdatedVendorAssignment;
  }

  /** Removes a single assignee (the CM's self-assignment or one trade partner) from an issue. */
  async removeVendorFromIssue<TIssue extends JobIssue>(
    issue: TIssue,
    vendorToRemove: JobIssueVendor,
    jobIssueVendors: WritableSignal<JobIssueVendor[] | undefined>,
    issues: WritableSignal<TIssue[] | undefined>,
    selfAssignmentService: SelfAssignmentService<TIssue> = this.jobIssueService
  ): Promise<void> {
    const didRemoveAssignToMe = vendorToRemove.externalVendorId === SELF_ASSIGNED_VENDOR.id;

    if (didRemoveAssignToMe) {
      await selfAssignmentService.unassignIssueFromMe(issue);
      this.setIssueSelfAssigned(issue.identifier, false, issues);
      return;
    }

    const removedVendorAssignment = await this.jobIssueVendorService.addOrReplaceVendorAssignmentForIssue(
      issue.identifier,
      null,
      vendorToRemove
    );

    jobIssueVendors.update(vendors => vendors?.filter(v => v.issueVendorIdentifier !== removedVendorAssignment.issueVendorIdentifier));
  }

  private setIssueSelfAssigned<TIssue extends JobIssue>(
    issueIdentifier: string,
    isSelfAssigned: boolean,
    issues: WritableSignal<TIssue[] | undefined>
  ): void {
    issues.update(current => current?.map(i => (i.identifier === issueIdentifier ? { ...i, isSelfAssigned } : i)));
  }
}
